import React from "react";
import { Link } from "react-router-dom";
import { Camera, Search, Lock, Share2, Mountain, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1554304246-9091463b887e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHw0fHxmcmllbmRzJTIwbGF1Z2hpbmclMjB2aW50YWdlJTIwcG9sYXJvaWQlMjBzdHlsZSUyMHBob3RvfGVufDB8fHx8MTc4MjY2MzE1NHww&ixlib=rb-4.1.0&q=85";
const ALBUM_1 = "https://images.unsplash.com/photo-1602692095685-d571be22806c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxmcmllbmRzJTIwbGF1Z2hpbmclMjB2aW50YWdlJTIwcG9sYXJvaWQlMjBzdHlsZSUyMHBob3RvfGVufDB8fHx8MTc4MjY2MzE1NHww&ixlib=rb-4.1.0&q=85";
const ALBUM_2 = "https://images.unsplash.com/photo-1494548162494-384bba4ab999?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxsYW5kc2NhcGUlMjBzdW5zZXQlMjBtZW1vcmllc3xlbnwwfHx8fDE3ODI2NjMxNTR8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { user } = useAuth();
  const authed = user && typeof user === "object";

  return (
    <div className="paper-grain min-h-screen" data-testid="landing-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <p className="font-script text-2xl text-[#8C3A3A] mb-4" data-testid="hero-tagline">
            for the photos you've never seen
          </p>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] mb-6">
            The pictures of <em className="italic text-[#8C3A3A]">you</em><br/>
            you don't have yet.
          </h1>
          <p className="text-lg text-[#5C5C5C] max-w-xl leading-relaxed mb-8">
            MemoryPeaks lets friends, classmates and old neighbors find you and post
            the pictures of you they've been keeping in shoeboxes and old phones.
            You decide what stays private and what becomes a memory.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            {authed ? (
              <Link to="/dashboard" className="btn-primary" data-testid="hero-cta-dashboard">
                Go to my Memories <ArrowRight className="w-4 h-4"/>
              </Link>
            ) : (
              <>
                <Link to="/register" className="btn-primary" data-testid="hero-cta-signup">
                  Claim your name <ArrowRight className="w-4 h-4"/>
                </Link>
                <Link to="/login" className="btn-secondary" data-testid="hero-cta-login">
                  Log in
                </Link>
              </>
            )}
            <Link to="/search" className="btn-ghost" data-testid="hero-cta-search">
              <Search className="w-4 h-4 mr-1"/> Find a friend
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5 relative h-[480px] hidden lg:block">
          {/* Polaroid stack */}
          <div className="absolute top-0 left-4 polaroid tilt-l w-64 fade-up delay-1">
            <span className="tape"/>
            <img src={HERO_IMG} alt="friends" className="w-full h-56 object-cover"/>
            <p className="font-script text-xl mt-2 text-center text-[#1F1F1F]">summer of '07</p>
          </div>
          <div className="absolute top-16 right-0 polaroid tilt-r w-64 fade-up delay-3">
            <img src={ALBUM_1} alt="silhouettes" className="w-full h-56 object-cover"/>
            <p className="font-script text-xl mt-2 text-center text-[#1F1F1F]">graduation day</p>
          </div>
          <div className="absolute bottom-0 left-16 polaroid tilt-xl w-64 fade-up delay-4">
            <span className="tape"/>
            <img src={ALBUM_2} alt="sunset" className="w-full h-56 object-cover"/>
            <p className="font-script text-xl mt-2 text-center text-[#1F1F1F]">road trip, somewhere</p>
          </div>
        </div>
      </section>

      <hr className="divider-stitch max-w-7xl mx-auto"/>

      {/* 4-COLUMN ACTIONS - mirrors the requested front-page features */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <p className="font-script text-2xl text-[#3A5A40] mb-2">where would you like to begin?</p>
        <h2 className="font-serif-display text-3xl sm:text-4xl mb-10">Four ways into the album.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <ActionCard to={authed ? "/dashboard" : "/login"} icon={<Camera className="w-6 h-6"/>}
                      title="View my album" body="See your pending photos and curate them into albums." testid="action-my-album"/>
          <ActionCard to="/search" icon={<Search className="w-6 h-6"/>}
                      title="View a friend's album" body="Find someone by screen name and browse what they've shared." testid="action-friend-album"/>
          <ActionCard to={authed ? "/post" : "/login"} icon={<Share2 className="w-6 h-6"/>}
                      title="Post to an album" body="Upload that picture of a friend you've been holding onto." testid="action-post"/>
          <ActionCard to={authed ? "/dashboard" : "/register"} icon={<Lock className="w-6 h-6"/>}
                      title="Sign up / Log in" body="Claim your screen name and start receiving memories." testid="action-signup"/>
        </div>
      </section>

      <hr className="divider-stitch max-w-7xl mx-auto"/>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-3 gap-8">
        <StepCard num="01" title="Claim a screen name"
                  body="Tell the world how to find you. Add as much or as little detail as you like — phone, hometown, schools — and choose what's searchable."/>
        <StepCard num="02" title="Invite the right people"
                  body="Share an invite link with old roommates, classmates, family. They post the pictures of you they've held onto."/>
        <StepCard num="03" title="Curate at your pace"
                  body="Every photo arrives private. View it, then decide: delete, keep private, or send it to a public album."/>
      </section>

      <section className="bg-[#3A5A40] text-[#F9F6F0] py-20 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Mountain className="w-10 h-10 mx-auto mb-4 opacity-80"/>
          <h2 className="font-serif-display text-4xl sm:text-5xl mb-4">Some memories aren't yours to keep alone.</h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            They're scattered across other people's phones, drives, and shoeboxes.
            MemoryPeaks brings them home.
          </p>
          {!authed && (
            <Link to="/register" className="inline-flex items-center gap-2 bg-[#F9F6F0] text-[#3A5A40] px-8 py-4 font-semibold hover:bg-white transition-colors" data-testid="cta-bottom-signup">
              Start your album <ArrowRight className="w-4 h-4"/>
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}

function ActionCard({ to, icon, title, body, testid }) {
  return (
    <Link to={to} className="paper-card p-6 block transition-transform hover:-translate-y-1 hover:shadow-xl" data-testid={testid}>
      <div className="w-12 h-12 flex items-center justify-center mb-4" style={{ background: "var(--mp-paper)", color: "var(--mp-terracotta)" }}>
        {icon}
      </div>
      <h3 className="font-serif-display text-xl mb-2">{title}</h3>
      <p className="text-sm text-[#5C5C5C] leading-relaxed">{body}</p>
    </Link>
  );
}

function StepCard({ num, title, body }) {
  return (
    <div className="border-l-2 border-[#8C3A3A] pl-6">
      <p className="font-script text-3xl text-[#8C3A3A]">{num}</p>
      <h3 className="font-serif-display text-2xl mb-2 mt-2">{title}</h3>
      <p className="text-[#5C5C5C] leading-relaxed">{body}</p>
    </div>
  );
}
